import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { landListingsAPI } from '../../services/landListingsAPI';

// Async thunk for fetching land listings
export const fetchLandListings = createAsyncThunk(
  'landListings/fetchLandListings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await landListingsAPI.getAllListings(params);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch land listings');
    }
  }
);

// Async thunk for fetching a single land listing
export const fetchLandListingById = createAsyncThunk(
  'landListings/fetchLandListingById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await landListingsAPI.getListingById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch land listing');
    }
  }
);

// Async thunk for fetching property types
export const fetchPropertyTypes = createAsyncThunk(
  'landListings/fetchPropertyTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await landListingsAPI.getPropertyTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch property types');
    }
  }
);

// Homepage listings are cached so repeat visits render cards instantly while a
// fresh copy is fetched in the background. Only unfiltered fetches are cached.
const FEATURED_CACHE_KEY = 'featured_listings_cache_v1';
const FEATURED_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;

const readFeaturedCache = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(FEATURED_CACHE_KEY));
    if (cached && Array.isArray(cached.data) && Date.now() - cached.savedAt < FEATURED_CACHE_MAX_AGE) {
      return cached.data;
    }
  } catch { }
  return [];
};

const writeFeaturedCache = (data) => {
  try {
    localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
  } catch { }
};

const isUnfilteredFetch = (params = {}) =>
  Object.keys(params).every((key) => key === 'limit');

const initialState = {
  listings: [],
  featuredListings: readFeaturedCache(),
  currentListing: null,
  propertyTypes: [],
  loading: false,
  error: null,
  pagination: {
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  },
  filters: {
    location: '',
    property_type: '',
    min_price: null,
    max_price: null,
    min_area: null,
    max_area: null,
  },
};

const landListingsSlice = createSlice({
  name: 'landListings',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentListing: (state) => {
      state.currentListing = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all land listings
      .addCase(fetchLandListings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLandListings.fulfilled, (state, action) => {
        state.loading = false;

        const listings = action.payload.data || [];

        // Add lastAerialUpdate field based on listing ID
        const enrichedListings = listings.map(listing => {
          // Aloor Layout - has aerial update
          if (listing.id === 'dbf1e4bd-b2e0-450f-82f2-7aedca2744f3') {
            return { ...listing, lastAerialUpdate: '12/08/2025' };
          }
          // Siddeshwar Layout - no aerial view available
          if (listing.id === '2f3e4d5c-6a7b-8c9d-0e1f-2a3b4c5d6e7f') {
            return { ...listing, lastAerialUpdate: null };
          }
          // Default: no aerial view available for other listings
          return { ...listing, lastAerialUpdate: null };
        });

        state.listings = enrichedListings;
        state.pagination = action.payload.pagination || initialState.pagination;
        state.filters = action.payload.filters || state.filters;

        // For now, show all listings (you can change this back to filter only approved ones later)
        // Filter only approved listings and set featured listings
        const approvedListings = enrichedListings.filter(listing => {
          return listing.approvalStatus === 'approved';
        });

        // Temporarily show all listings if no approved ones, or show approved ones
        state.featuredListings = approvedListings.length > 0 ? approvedListings : enrichedListings;
        if (isUnfilteredFetch(action.meta.arg)) writeFeaturedCache(state.featuredListings);
      })
      .addCase(fetchLandListings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch single land listing
      .addCase(fetchLandListingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLandListingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentListing = action.payload.data;
      })
      .addCase(fetchLandListingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch property types
      .addCase(fetchPropertyTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPropertyTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.propertyTypes = action.payload.data || [];
      })
      .addCase(fetchPropertyTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentListing } = landListingsSlice.actions;

export default landListingsSlice.reducer;
